import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ClipboardText, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "booked", "client", "not_a_fit"];
const label = (s) => (s || "").replace(/_/g, " ");

const IntakeCard = ({ item, onSave }) => {
  const [notes, setNotes] = useState(item.notes || "");
  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid={`fcs-intake-row-${item.id}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-white font-bold text-lg">{item.business_name}</p>
            <p className="text-zinc-400 text-sm">{item.name} · {item.phone} · {item.email}</p>
          </div>
          <select
            value={item.status}
            onChange={(e) => onSave(item.id, { status: e.target.value })}
            data-testid={`fcs-intake-status-${item.id}`}
            className="bg-zinc-900 border border-zinc-700 text-white text-sm px-2 py-1 capitalize"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 text-sm">
          <div><p className="text-zinc-500 text-xs">Referred by</p><p className="text-emerald-400">{item.referred_by || "—"}</p></div>
          <div><p className="text-zinc-500 text-xs">SAM.gov</p><p className="text-zinc-200 capitalize">{label(item.sam_status)}</p></div>
          <div><p className="text-zinc-500 text-xs">Goal</p><p className="text-zinc-200 capitalize">{label(item.main_goal)}</p></div>
          <div><p className="text-zinc-500 text-xs">Best time</p><p className="text-zinc-200">{item.best_time || "—"}</p></div>
        </div>
        <p className="text-zinc-300 text-sm whitespace-pre-line">{item.business_description}</p>
        <div className="flex gap-2 items-end">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Your notes" rows={2} className="bg-zinc-900 border-zinc-700 text-white" data-testid={`fcs-intake-notes-${item.id}`} />
          <Button size="sm" onClick={() => onSave(item.id, { notes })} data-testid={`fcs-intake-save-notes-${item.id}`}>Save</Button>
        </div>
        <p className="text-zinc-600 text-xs">{new Date(item.created_at).toLocaleString()}</p>
      </CardContent>
    </Card>
  );
};

export default function AdminFcsIntakes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const formUrl = `${window.location.origin}/fcs/intake`;

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/fcs-intakes`);
      setItems(res.data.intakes || []);
    } catch {
      toast.error("Failed to load intakes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (id, fields) => {
    try {
      await axios.patch(`${API}/admin/fcs-intakes/${id}`, fields);
      toast.success("Saved");
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-fcs-intakes-page">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3">
            <ClipboardText weight="duotone" className="w-8 h-8 text-emerald-500" /> FCS Intakes
          </h1>
          <p className="text-zinc-400 mt-1">Prospects from the Federal Contract Sprints intake form.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <code className="bg-zinc-900 text-zinc-300 px-3 py-2" data-testid="fcs-form-url">{formUrl}</code>
          <Button size="sm" variant="outline" data-testid="fcs-copy-form-link" onClick={() => { navigator.clipboard.writeText(formUrl); toast.success("Link copied"); }}>
            <Copy className="mr-1" /> Copy form link
          </Button>
        </div>
        {loading ? <p className="text-zinc-500">Loading...</p> : items.length === 0 ? (
          <p className="text-zinc-500" data-testid="fcs-intakes-empty">No intakes yet.</p>
        ) : items.map((it) => <IntakeCard key={it.id} item={it} onSave={save} />)}
      </div>
    </AdminLayout>
  );
}
